import { home as zhHome } from '../zh-CN/home'

const detailBox = (title, content) => ({
  title,
  illustrate: [{ id: '01', illustrateContent: content }],
  illustrateWarn: ''
})

const dataChallenges = [
  {
    introTitle: 'Stability of Credit Risk Scoring',
    intro: 'Build a credit-risk scoring model that combines strong discriminatory power with stable outputs. The model should capture differences in customer qualifications, operations and liabilities while maintaining reasonable consistency when core customer characteristics remain unchanged.'
  },
  {
    introTitle: 'Business-customer Risk Control with Knowledge Graphs',
    intro: 'Build a risk-scoring model for business customers using relationship-graph data, including upstream and downstream partners, shareholders, controllers, investors and time-series risk events. The goal is to identify hidden risk-transmission paths and improve prediction accuracy.'
  },
  {
    introTitle: 'SME Operating-risk Indicators Using Public Data',
    intro: 'Explore how legally accessible public data can complement transaction-flow data and create a richer risk profile for small and micro enterprises, improving both the accuracy and forward-looking value of credit-risk assessment.'
  },
  {
    introTitle: 'Detection of Irregular Trading Activities',
    intro: 'Develop an algorithm to identify order brushing, fabricated transactions and falsified account flows used to inflate credit eligibility. Extract measurable patterns that distinguish false transactions from normal business activity.'
  },
  {
    introTitle: 'Stock-return Prediction from Raw Price-volume Data',
    intro: `<a class="data-source-highlight" href="https://pan.baidu.com/s/1aR4M-Q_vkr8gQrXstMXWgg?pwd=ea9b" target="_blank" rel="noopener noreferrer" aria-label="Open Baidu Netdisk to download daily price-volume data for the mainland China A-share market, 324 MB">Using daily price-volume data for the mainland China A-share market</a>, build a model that predicts each stock's one-day-ahead return. For every ts_code and trade_date record in the test set, predict y_ret_1d.`
  }
].map((item, index) => ({
  ...item,
  ...(index === 4
    ? {
        dataDownload: {
          url: 'https://pan.baidu.com/s/1aR4M-Q_vkr8gQrXstMXWgg?pwd=ea9b',
          label: 'Download Challenge Data',
          meta: 'Baidu Netdisk · 324 MB',
          ariaLabel: 'Open Baidu Netdisk to download the challenge data, 324 MB'
        }
      }
    : {}),
  detailsText: detailBox('View Full Challenge', 'Open the full challenge brief for the background, objectives, requirements, submission format and evaluation notes.')
}))

export const home = {
  ...zhHome,
  introduction: {
    ...zhHome.introduction,
    title: 'ABOUT THE COMPETITION',
    subTitle: 'FINTECHATHON 2026',
    intro: [
      '<span class="intro-paragraph">The 2026 Shenzhen International FinTech Competition — WeBank FinTech University Challenge is held with Shenzhen Municipal Local Financial Administration, Shenzhen University and WeBank jointly serving as strategic guidance units and the Shenzhen University–WeBank Institute of FinTech as host. <strong class="intro-highlight">The competition has been successfully held for eight consecutive editions</strong>, producing thousands of outstanding projects, attracting nearly 10,000 university students from China and abroad, and continuously advancing technological innovation in fintech, talent development, and industry–academia–research collaboration.</span><span class="intro-paragraph">In response to the global momentum of artificial intelligence, <strong class="intro-highlight">the competition is comprehensively upgrading its International Track (Artificial Intelligence direction)</strong>. With the 2026 APEC Summit set to take place in Shenzhen, the competition is seizing this opportunity to address the profound transformations that AI technologies are bringing to global economic and social development, upgrade its format with a more open international outlook, and expand recruitment to universities worldwide.</span><span class="intro-paragraph">The competition has invited the <strong class="intro-highlight">CCF Technical Committee on Digital Finance, Tsinghua Institute of Financial Technology, IEEE Computational Intelligence Society Singapore Chapter and IEEE Computational Intelligence Society Shenzhen Chapter</strong> to serve as technical guidance units. A group of industry experts will serve as academic advisers and judges, providing participating teams with end-to-end professional support from competition format design through entry evaluation. We warmly invite university students from China and around the world to explore the innovative future of fintech and artificial intelligence together!</span>'
    ]
  },
  bars: [
    { name: 'About', id: '#introduction' },
    { name: 'Challenges', id: '#note' },
    { name: 'Schedule', id: '#schedule' },
    { name: 'Awards', id: '#awards' },
    { name: 'Advisers', id: '#judge' },
    { name: 'Partners', id: '#partners' }
  ],
  barQrcodeList: zhHome.barQrcodeList.map((item, index) => ({
    ...item,
    name: index === 0 ? 'AI Assistant' : 'Data Analytics Assistant'
  })),
  nav: [
    { title: 'Challenges', name: 'Home', id: 1 },
    { title: 'Rules', name: 'Rule', id: 2 },
    {
      title: 'Organisers',
      name: 'Home',
      id: 5,
      children: [
        { title: 'Shenzhen University–WeBank Institute of FinTech', name: 'https://swift.szu.edu.cn/index.htm', id: 5.1 },
        { title: 'WeBank', name: 'https://www.webank.com/', id: 5.2 }
      ]
    }
  ],
  note: [
    {
      region: 'domestic',
      type: 'ai',
      logo: zhHome.note[0].logo,
      title: 'Artificial Intelligence Track',
      introTitle: 'Banking AI Agent',
      subjectDetailKey: 'aiDomesticEn',
      intro: 'The Domestic Track focuses on “AI operating banks on behalf of users” (safety + experience). Teams must build an AI Banking Agent that understands financial intent through natural-language dialogue, autonomously plans and executes banking operations, and connects with users through channels such as apps and IM. The system must cover at least three core scenarios; full coverage is encouraged.',
      scenarios: [
        { index: '01', title: 'Smart Transfers', text: 'Transfer by name, phone number or note; schedule transfers; split-bill collection' },
        { index: '02', title: 'Bill Analysis', text: 'Spending categories; unusual-transaction detection; monthly / annual reports' },
        { index: '03', title: 'Wealth Operations', text: 'Product recommendation and comparison; risk assessment; subscribe / redeem' },
        { index: '04', title: 'Card Management', text: 'Card application; limit adjustment; transaction restrictions / unlock; card loss reporting' },
        { index: '05', title: 'Subscriptions / Direct Debit', text: 'Detect recurring charges; renewal reminders; one-click cancellation' },
        { index: '06', title: 'Cross-scenario Orchestration', text: 'Recognise a birthday need, reserve funds and automatically order flowers, a cake and other services' }
      ],
      moreBtn: {
        details: 'Challenge Brief',
        detailsText: detailBox('Banking AI Agent', 'Design and build a financial AI Agent that can understand intent, plan tasks and safely execute banking operations while maintaining user control and transparent risk safeguards.')
      }
    },
    {
      region: 'domestic',
      type: 'blockchain',
      logo: zhHome.note[1].logo,
      title: 'Data Analytics Track',
      subtitle: 'Five challenges based on real fintech scenarios assess data sourcing, analytical modelling and business application. Teams select one challenge.',
      intro: 'Choose one of five data analytics challenges.',
      moreBtn: {
        tabText: ['Challenge 1', 'Challenge 2', 'Challenge 3', 'Challenge 4', 'Challenge 5'],
        details: 'View Full Challenge',
        tabInfoArr: dataChallenges
      }
    },
    {
      region: 'international',
      type: 'ai',
      logo: zhHome.note[2].logo,
      title: 'Artificial Intelligence Track',
      introTitle: 'AI as a Financial Participant',
      intro: 'All directions address the theme “AI as a Financial Participant”. Teams select one direction for in-depth implementation. Directions A–E are new or expanded this year; Direction F is the original Agent Wallet.',
      directions: [
        { index: 'A', title: 'AI Personal Finance Assistant', text: 'Conversational bookkeeping, budgeting and bill management' },
        { index: 'B', title: 'AI Wealth Assistant', text: 'Risk profiling, asset allocation and simulated investment planning' },
        { index: 'C', title: 'Cross-border / International Student Finance Assistant', text: 'Multi-currency, exchange rates, remittances and multilingual service' },
        { index: 'D', title: 'AI Insurance Assistant', text: 'Policy interpretation, plan recommendations and claims guidance' },
        { index: 'E', title: 'Small-business Finance Assistant', text: 'Cash flow, reconciliation and operational recommendations' },
        { index: 'F', title: 'Agent Wallet', text: 'An AI-owned wallet that can spend on-chain' }
      ],
      moreBtn: {
        details: 'Challenge Brief',
        detailsText: detailBox('AI as a Financial Participant', 'Select one direction and deliver a working financial Agent that demonstrates intent understanding, task planning, safe execution, user control and measurable value in a financial scenario.')
      }
    }
  ],
  schedule: {
    title: { h1: 'COMPETITION SCHEDULE' },
    timezone: 'All dates follow China Standard Time (UTC+8).',
    common: [
      { date: '31 AUG', title: 'Registration Opens' },
      { date: '15 SEP', title: 'First Online Livestream' },
      { date: '17 SEP', title: 'Second Online Livestream' }
    ],
    tracks: [
      {
        key: 'domestic-ai',
        label: 'Domestic AI Track',
        events: [
          { date: '28 OCT', title: 'Online Q&A' },
          { date: '2 NOV', title: 'Submission Deadline' },
          { date: '10 NOV', title: 'Finalists Announced' },
          { date: '28–29 NOV', title: 'AI Hackathon' }
        ]
      },
      {
        key: 'data',
        label: 'Data Analytics Track',
        events: [
          { date: '12 OCT', title: 'Preliminary Submission Deadline' },
          { date: '18 OCT', title: 'Semi-finalists Announced' },
          { date: '2 NOV', title: 'Semi-final Submission Deadline' },
          { date: '10 NOV', title: 'Finalists Announced' },
          { date: '29 NOV', title: 'On-site Data Analytics Defence' }
        ]
      },
      {
        key: 'international-ai',
        label: 'International AI Track',
        featured: true,
        events: [
          { date: '20 OCT', title: 'Registration & Submission Deadline' },
          { date: '21 OCT', title: 'Compliance Screening' },
          { date: '23 OCT', title: 'Online Review' },
          { date: '31 OCT', title: 'Finalists Announced · Invitation Letters Issued' },
          { date: '27 NOV', title: 'Check-in · Visit to WeBank' },
          { date: '29 NOV', title: 'On-site Defence' }
        ]
      }
    ],
    finale: { date: '30 NOV', title: 'Awards Ceremony', place: 'Shenzhen, China' },
    subTitle: '*If force majeure or other external factors require changes to the results, final format, competition format or defence rules, the Organising Committee will notify participants through the official website, email and the competition assistants.'
  },
  prize: {
    ...zhHome.prize,
    desc: '',
    highlights: {
      title: 'COMPETITION HIGHLIGHTS',
      prizeLabel: 'TOTAL PRIZE POOL',
      prizeValue: '750,000',
      prizeUnit: 'RMB',
      prizeMeta: '',
      featureTitle: 'A PROFESSIONAL COMPETITION SYSTEM',
      features: [
        { index: '01', title: 'Eight Years of Experience', text: 'Eight consecutive editions have generated thousands of outstanding projects and attracted nearly 10,000 students from leading universities worldwide.' },
        { index: '02', title: 'Authoritative Technical Guidance', text: 'Technical guidance is provided by the CCF Technical Committee on Digital Finance, Tsinghua Institute of Financial Technology and the IEEE CIS Singapore and Shenzhen Chapters.' },
        { index: '03', title: 'International Competition Format', text: 'The English-language International AI Track recruits from universities worldwide, encouraging global exchange among young innovators.' },
      ],
      awardSubtitle: '',
      awardTitle: '',
      awardMeta: '',
      note: 'The Domestic AI, International AI and Data Analytics tracks each award one First Prize, Second Prize, Third Prize and Best Creative Award. Each track offers RMB 250,000, bringing the total prize pool to RMB 750,000. Winning teams receive physical certificates and trophies. All cash awards are pre-tax.',
      awards: [
        { rank: 'First Prize', amount: 'RMB 100,000' },
        { rank: 'Second Prize', amount: 'RMB 80,000' },
        { rank: 'Third Prize', amount: 'RMB 50,000' },
        { rank: 'Best Creative Award', amount: 'RMB 20,000' }
      ]
    }
  },
  judges: [{
    title: { h1: 'INVITED ACADEMIC ADVISERS', tip: '' },
    list: [
      { ...zhHome.judges[0].list[0], name: 'Wang Shouyang', title: 'Distinguished Researcher, Chinese Academy of Sciences; Academician, The World Academy of Sciences; Academician, International Academy for Systems and Cybernetic Sciences; National High-Level Talent Program; NSFC Distinguished Young Scholar' },
      { ...zhHome.judges[0].list[1], name: 'Qiang Yang', title: 'Chief AI Adviser, WeBank; Fellow, Royal Society of Canada; Fellow, Canadian Academy of Engineering' },
      { ...zhHome.judges[0].list[2], name: 'Li Wenrong', headImg: zhHome.judges[0].list[2].headImg, title: 'Vice-President, City University of Hong Kong; Fellow, U.S. National Academy of Artificial Intelligence; International Fellow, Canadian Academy of Engineering' },
      { ...zhHome.judges[0].list[3], name: 'Li Jianqiang', title: 'Dean, College of Artificial Intelligence, Shenzhen University; Executive Director, National Engineering Laboratory for Big Data System Computing Technology; NSFC Distinguished Young Scholar' },
      { ...zhHome.judges[0].list[4], name: 'Chai Yunpeng', headImg: zhHome.judges[0].list[4].headImg, title: 'Dean and Deputy Party Secretary, School of Information, Renmin University of China; Professor and Doctoral Supervisor' },
      { ...zhHome.judges[0].list[5], name: 'Dacheng Xiu', headImg: zhHome.judges[0].list[5].headImg, title: 'Joseph Sondheimer Professor of Econometrics, Statistics and Finance, Chicago Booth, University of Chicago' },
      { ...zhHome.judges[0].list[6], name: 'Wolfgang Karl Härdle', headImg: zhHome.judges[0].list[6].headImg, title: 'Tenured Professor, School of Business and Economics, Humboldt-Universität zu Berlin; Director, Data Research Centre and Digital Assets Research Institute' },
      { ...zhHome.judges[0].list[7], name: 'Joshua Bongard', headImg: zhHome.judges[0].list[7].headImg, title: 'Veinott Professor of Computer Science, University of Vermont; Director, Morphology, Evolution & Cognition Laboratory' }
    ]
  }],
  news: [
    { titleCh: 'LATEST NEWS', titleEn: 'NEWS', content: { list: [] } },
    {
      titleCh: 'LATEST NEWS',
      titleEn: 'NEWS',
      content: {
        list: [
          {
            id: '01',
            text: '2026 Shenzhen International FinTech Competition launches its International Track',
            link: 'https://r10.ieee.org/shenzhen-cis/blog/2026/06/17/2026-shenzhen-international-fintech-competition-international-track/'
          },
          {
            id: '02',
            text: '2025 Shenzhen International FinTech Competition concludes with industry–academia collaboration',
            link: 'https://www.peopleapp.com/column/30051004536-500007261769'
          },
          {
            id: '03',
            text: '2024 Shenzhen International FinTech Competition concludes, highlighting new fintech talent',
            link: 'https://www.fisco.com.cn/news_7/962.html'
          },
          {
            id: '04',
            text: '2023 Shenzhen International FinTech Competition concludes at Shenzhen University',
            link: 'https://news.szu.edu.cn/info/1003/10161.htm'
          },
          {
            id: '05',
            text: 'HKUST(GZ) students win first prize at the 2025 Shenzhen International FinTech Competition',
            link: 'https://www.hkust-gz.edu.cn/zh/?p=13630%2F'
          },
          {
            id: '06',
            text: 'Nanjing University students receive the Best Creative Award at FinTechathon',
            link: 'https://sdem.nju.edu.cn/b0/99/c56973a831641/pagem.htm'
          }
        ]
      }
    }
  ],
  videos: {
    titleCh: 'EVENT HIGHLIGHTS',
    titleEn: '',
    list: zhHome.videos.list.map((item, index) => ({
      ...item,
      contentText: [
        '2026 Shenzhen International FinTech Competition — Technical Open Class (Session 1)',
        '2026 Shenzhen International FinTech Competition — Technical Open Class (Session 2)',
        '2025 Shenzhen International FinTech Competition & WeBank Campus Recruitment Session',
        '2025 Shenzhen International FinTech Competition — Technical Open Class'
      ][index] || item.contentText
    })),
    buttonText: '',
    buttonLink: ''
  },
  partners: [
    { title: 'Strategic Guidance', list: ['Shenzhen Municipal Local Financial Administration', 'Shenzhen University', 'WeBank'] },
    { title: 'Host', list: ['Shenzhen University–WeBank Institute of FinTech'] },
    { title: 'Organisers', list: ['iCAN Future (Beijing) Technology Co., Ltd.', 'GouXiongHui'] },
    {
      title: 'Technical Advisers',
      list: [
        'CCF Technical Committee on Digital Finance',
        'Tsinghua Institute of Financial Technology',
        'IEEE Computational Intelligence Society Singapore Chapter',
        'IEEE Computational Intelligence Society Shenzhen Chapter'
      ]
    }
  ]
}
